import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { getCurrentUser } from '../utils/auth';
import { hasAnyPermission } from '../utils/permissions';

/**
 * ProtectedRoute — guards routes by usertype and optionally by permission.
 *
 * Props:
 *   allowed: string[]  — allowed usertypes (e.g. ['superadmin', 'admin'])
 *   permissions: string[] — optional permission keys required (OR logic).
 *     If provided, admin users must have at least one of these permissions.
 *     superadmin always passes. Non-admin users are not affected by this.
 */
const ProtectedRoute = ({ allowed = [], permissions = [] }) => {
  const user = getCurrentUser();
  const location = useLocation();
  if (!user) {
    // Carry the requested page so login can land the user where they were
    // headed — deep links from emails (e.g. "Join Live Class") depend on it.
    const dest = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(dest)}`} replace />;
  }
  if (allowed.length > 0 && !allowed.includes(user.usertype)) {
    return <Navigate to="/not-authorized" replace />;
  }
  // Permission check for admin users (tenantadmin bypasses)
  if (permissions.length > 0 && (user.usertype === 'admin' || user.usertype === 'superadmin')) {
    if (!hasAnyPermission(...permissions)) {
      return <Navigate to="/not-authorized" replace />;
    }
  }
  return <Outlet />;
};

export default ProtectedRoute;
